import React from 'react';
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { getWordOrdersAPI, submitInspectionDataAPI } from "../services/wordOrderAPI";
import { InspectionData, WorkOrderStatus } from "../../types/workOrder";
import { AppDispatch, RootState } from "../store";
import { showToaster } from "./toasterSlice";
import { isReallyOnline } from "../../lib/isOnline";
import { toast } from "react-toastify";

export interface WorkOrder {
  workOrder_ID: number;
  account_ID: number;
  package_ID: number;
  saleFormat_ID: number;
  accountName: string;
  package_Name: string;
  salesRep: string;
  workOrderStatus_ID: number;
  workOrderStatus: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  requestedStartDate: string;
  requestedEndDate: string;
  location_ID: number;
  location: string;
  location_Contact_Name: string;
  location_Phone: string;
  completedAssetCount: number;
  assetCount: number;
}

export interface WorkOrderState {
  workOrders: WorkOrder[];
  totalWorkOrders: number;
  loading: boolean;
  error: string | null;
}

const initialState: WorkOrderState = {
  workOrders: [],
  totalWorkOrders: 0,
  loading: false,
  error: null,
};

export const fetchWorkOrders = createAsyncThunk(
  "workOrders/fetchWorkOrders",
  async (token: string, { rejectWithValue }) => {
    try {
      const workOrders = await getWordOrdersAPI(token);
      return workOrders;
    } catch (error) {
      console.log("[Error in Fetching work orders]:: ", error);
      return rejectWithValue("Failed to fetch work orders");
    }
  }
);

export const submitInspectionData = createAsyncThunk<
  string,
  { token: string | null; inspectionData: InspectionData; workOrder_ID: number },
  { rejectValue: string; state: RootState; dispatch: AppDispatch }
>(
  'workOrders/submitInspectionData',
  async (
    { inspectionData,
      workOrder_ID, token },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const result = await submitInspectionDataAPI({
        token,
        inspectionData,
        workOrder_ID
      });
      const isOnline = await isReallyOnline()
      dispatch(showToaster({ message: isOnline ? `Workorder submitted successfully!`: `Workorder submitted offline!`, type: "success" }));
      return result;
    } catch (error: any) {
      // Extract error message, handling both string and object errors
      let errorMessage = "Error while submitting.";
      
      console.log('[Full error object]:: ', error);
      
      // Check if the error object itself contains the message from the backend
      if (error && typeof error.message === 'object') {
        // Check for both uppercase and lowercase property names
        if ((error.message.Success === false || error.message.success === false) && 
            (error.message.Message || error.message.message)) {
          // This handles when the axios interceptor has already extracted the response data
          errorMessage = error.message.Message || error.message.message;
        }
      }
      // Check if the error message is a string that might be JSON
      else if (error && typeof error.message === 'string') {
        try {
          // Try to parse it as JSON in case it's a stringified object
          const parsedMessage = JSON.parse(error.message);
          if ((parsedMessage.Success === false || parsedMessage.success === false) && 
              (parsedMessage.Message || parsedMessage.message)) {
            errorMessage = parsedMessage.Message || parsedMessage.message;
          } else {
            errorMessage = error.message;
          }
        } catch (e) {
          // If it's not JSON, use the message as is
          errorMessage = error.message;
        }
      }
      // Check if the error has a response with data
      else if (error && error.response && error.response.data) {
        const responseData = error.response.data;
        if ((responseData.Success === false || responseData.success === false) && 
            (responseData.Message || responseData.message)) {
          errorMessage = responseData.Message || responseData.message;
        } else if (typeof responseData === 'string') {
          errorMessage = responseData;
        }
      }
      // Last resort
      else if (error && typeof error.toString === 'function') {
        const errorString = error.toString();
        errorMessage = errorString !== '[object Object]' ? errorString : "Unknown error occurred";
      }
      console.log('[Extracted error message]:: ', errorMessage);
      toast.error(
        React.createElement('div', { 
          dangerouslySetInnerHTML: { __html: errorMessage } 
        }), 
        {
          autoClose: false,  // Keep it open until user closes
          closeOnClick: false,
          draggable: false
        }
      );
      console.log('[Error in submission]:: ', error);
      return rejectWithValue(errorMessage);
    }
  }
);


const workOrderSlice = createSlice({
  name: "workOrders",
  initialState,
  reducers: {
    removeWorkOrder: (state) => {
      state.workOrders = [];
      state.totalWorkOrders = 0;
      state.loading = false;
      state.error = null;
    },
    updateCompletedAssetCount: (
      state,
      action: PayloadAction<{ workOrder_ID: number; change: number }>
    ) => {
      const { workOrder_ID, change } = action.payload;
      const workOrder = state.workOrders.find(
        (order) => order.workOrder_ID === workOrder_ID
      );
      if (workOrder) {
        workOrder.completedAssetCount += change;
      }
    },
    deleteWorkOrder: (
      state,
      action: PayloadAction<number>
    ) => {
      state.workOrders = state.workOrders.filter(
        (order) => order.workOrder_ID !== action.payload
      );
      state.totalWorkOrders -= 1;
    },
    incrementAssetCount: (state, action: PayloadAction<{ workOrder_ID: number }>) => {
      const { workOrder_ID } = action.payload;
      const workOrder = state.workOrders.find(order => order.workOrder_ID === workOrder_ID);
      if (workOrder) {
          workOrder.assetCount += 1;
      }
  },
  
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchWorkOrders.fulfilled,
        (state, action: PayloadAction<{ totalWorkOrders: number; workOrders: WorkOrder[] }>) => {
          state.loading = false;
          state.totalWorkOrders = action.payload.totalWorkOrders;
          state.workOrders = action.payload.workOrders;
        }
      )
      
      .addCase(fetchWorkOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(submitInspectionData.fulfilled, (state, action) => {
        const workOrder_ID = action.meta.arg.workOrder_ID;

        const workOrder = state.workOrders.find(
          (order) => order.workOrder_ID === workOrder_ID
        );

        if (workOrder) {
          workOrder.workOrderStatus = WorkOrderStatus.Completed;
        }
      });
  },
});

export const { removeWorkOrder, updateCompletedAssetCount, incrementAssetCount } = workOrderSlice.actions;
export default workOrderSlice.reducer;
